import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowsLeftRight, PaperPlaneTilt, CheckCircle, Trash, Package } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function ThirdPartyPanel({ projects, onChanged }) {
  const { hasRole } = useAuth();
  const canEdit = hasRole("pm", "inginer", "admin");
  const [projectId, setProjectId] = useState("");
  const [materials, setMaterials] = useState([]);
  const [sel, setSel] = useState({});
  const [transfers, setTransfers] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ third_party: "", operation: "zincare", notes: "" });

  const loadTransfers = () => api.get("/third-party").then((r) => setTransfers(r.data)).catch(() => {});
  useEffect(() => { loadTransfers(); }, []);
  useEffect(() => {
    if (!projectId) { setMaterials([]); return; }
    api.get(`/inventory?scope=project&project_id=${projectId}`).then((r) => setMaterials(r.data)).catch(() => {});
    setSel({});
  }, [projectId]);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const extract = async () => {
    try {
      await api.post("/third-party/extract", { project_id: projectId, item_ids: selectedIds, ...form });
      toast.success("Materiale predate la terț"); setDlg(false); setForm({ third_party: "", operation: "zincare", notes: "" }); setSel({}); loadTransfers(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const receive = async (id) => { try { await api.post(`/third-party/${id}/receive`, {}); toast.success("Primit înapoi"); loadTransfers(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/third-party/${id}`); loadTransfers(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div data-testid="thirdparty-panel">
      <p className="mb-3 text-sm text-muted-foreground">Extrage materiale din gestiunea unui proiect și predă-le la terți (zincare), cu urmărire predat → primit. <span className="text-amber-400">Date mock; conectarea la contabilitate se activează ulterior.</span></p>
      {canEdit && (
        <div className="mb-5 rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="max-w-md" data-testid="tp-project"><SelectValue placeholder="Alege proiect…" /></SelectTrigger>
              <SelectContent className="max-h-72">{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {selectedIds.length > 0 && <Button onClick={() => setDlg(true)} data-testid="tp-send-btn"><PaperPlaneTilt size={16} className="mr-1.5" /> Trimite {selectedIds.length} la terț</Button>}
          </div>
          {projectId && (
            <div className="space-y-1.5">
              {materials.map((m) => (
                <label key={m.id} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 text-sm hover:bg-white/5" data-testid={`tp-material-${m.id}`}>
                  <input type="checkbox" checked={!!sel[m.id]} onChange={() => toggle(m.id)} />
                  <Package size={16} className="text-muted-foreground" />
                  <span className="flex-1">{m.name} {m.code ? `· ${m.code}` : ""}</span>
                  <span className="text-muted-foreground">{m.quantity} {m.unit}</span>
                </label>
              ))}
              {materials.length === 0 && <p className="text-sm text-muted-foreground">Niciun material în gestiunea acestui proiect.</p>}
            </div>
          )}
        </div>
      )}

      <h3 className="mb-3 font-head text-base font-bold">Transferuri la terți</h3>
      <div className="space-y-2">
        {transfers.map((t) => (
          <div key={t.id} className="rounded-xl border border-border bg-card p-4" data-testid={`tp-transfer-${t.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-500/10 text-violet-400"><ArrowsLeftRight size={20} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold">{t.third_party || "Terț"}</span>
                  <span className="rounded bg-white/5 px-1.5 py-0.5 text-[11px]">{t.operation}</span>
                  <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${t.status === "primit" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{t.status === "primit" ? "Primit" : "Predat"}</span>
                </div>
                <div className="text-xs text-muted-foreground">{t.project_code} · {t.items?.length || 0} materiale · predat {fmt(t.handed_at)}{t.received_at ? ` · primit ${fmt(t.received_at)}` : ""}</div>
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  {t.status !== "primit" && <Button size="sm" variant="outline" className="text-emerald-400" onClick={() => receive(t.id)} data-testid={`tp-receive-${t.id}`}><CheckCircle size={15} className="mr-1" /> Primit</Button>}
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(t.id)}><Trash size={15} /></Button>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(t.items || []).map((it, i) => <span key={i} className="rounded bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">{it.name} · {it.quantity} {it.unit}</span>)}
            </div>
          </div>
        ))}
        {transfers.length === 0 && <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Niciun transfer la terți. Selectează materiale dintr-un proiect și trimite-le la zincare.</p>}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Predare la terț ({selectedIds.length} materiale)</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Terț (furnizor zincare)</label><Input value={form.third_party} onChange={(e) => setForm({ ...form, third_party: e.target.value })} placeholder="ex. Zinco SRL" data-testid="tp-thirdparty-name" /></div>
            <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Operație</label>
              <Select value={form.operation} onValueChange={(v) => setForm({ ...form, operation: v })}>
                <SelectTrigger data-testid="tp-operation"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="zincare">Zincare</SelectItem><SelectItem value="vopsire">Vopsire</SelectItem><SelectItem value="prelucrare">Prelucrare</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Note</label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={extract} data-testid="tp-extract-btn"><PaperPlaneTilt size={15} className="mr-1.5" /> Predă la terț</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
